/**
 * @author piotrzurek
 * @since 2022-7-25
 */

import * as React from "react";
import styled, { useTheme } from "styled-components";

import {
  BOTTOM_NAVIGATION_SPACE_KEEPER_HEIGHT,
  NAVIGATION_HEADER_SPACE_KEEPER_HEIGHT,
} from "@config/consts";

import { rem } from "@ui/helpers";
import { Button, Heading, OptionPanel } from "@ui/Atoms";
import { TestWrapper } from "@ui/Atoms/TestWrapper";
import { CaretLeft } from "@ui/Assets/Symbolicons";

import { useBreakpoint } from "@hooks/useBreakpoint";

interface Props {
  testId?: string;
  className?: string;
  children?: React.ReactNode;
  withBackNavigation?: boolean;
  footer?: ({
    handleBack,
  }: {
    handleBack?: () => void;
  }) => React.ReactElement | null;
  onBack?: () => void;
  translations: {
    backText: string;
  };
}

const FlowPanel = (props: Props): React.ReactElement => {
  const { mobile } = useBreakpoint("v1");
  const theme = useTheme();

  return (
    <TestWrapper testId={props.testId}>
      <FlowPanelWrapper className={props.className}>
        <Body>
          {props.withBackNavigation && mobile && (
            <ButtonRow>
              <TestWrapper testId="back-button">
                <BackButton onClick={props.onBack}>
                  <CaretLeft
                    size={theme.icons.sizes.base_x5}
                    variant="line"
                    color={theme.palette.grey800}
                  />
                </BackButton>
              </TestWrapper>
            </ButtonRow>
          )}
          <BodyContainer>{props.children}</BodyContainer>
        </Body>
        {(props.footer || (!mobile && props.withBackNavigation)) && (
          <Footer>
            <FooterContainer>
              {props.footer?.({ handleBack: props.onBack })}
              {!mobile && props.withBackNavigation && !props.footer && (
                <TestWrapper testId="back-button">
                  <Button
                    variant="textBlue"
                    size={mobile ? "fill" : "medium"}
                    onClick={props.onBack}
                  >
                    {props.translations.backText}
                  </Button>
                </TestWrapper>
              )}
            </FooterContainer>
          </Footer>
        )}
      </FlowPanelWrapper>
    </TestWrapper>
  );
};

const Title = styled(Heading).attrs<{
  size?: "small" | "large";
}>(props => ({
  styleAs: props.size === "small" ? "h6" : "h5",
}))<{
  size?: "small" | "large";
}>`
  text-align: center;
  line-height: ${rem(32)};
  font-weight: ${props => props.theme.fonts.weights.normal};
  margin-bottom: ${props => props.theme.margins.base_x2};

  ${props => props.theme.breakpoints.tablet} {
    max-width: ${rem(480)};
    margin: 0 auto ${props => props.theme.margins.base_x2};
  }
`;

const Description = styled.p`
  font-size: ${props => props.theme.fonts.sizes.body};
  margin-bottom: ${props => props.theme.margins.base_x4};
  text-align: center;
  color: ${props => props.theme.palette.grey600};

  ${props => props.theme.breakpoints.tablet} {
    max-width: ${rem(480)};
    margin: 0 auto ${props => props.theme.margins.base_x4};
  }
`;

const FullScreen = styled(FlowPanel)`
  min-height: calc(
    100vh - ${rem(NAVIGATION_HEADER_SPACE_KEEPER_HEIGHT)} -
      ${rem(BOTTOM_NAVIGATION_SPACE_KEEPER_HEIGHT)}
  );
  min-height: calc(
    100dvh - ${rem(NAVIGATION_HEADER_SPACE_KEEPER_HEIGHT)} -
      ${rem(BOTTOM_NAVIGATION_SPACE_KEEPER_HEIGHT)}
  );

  ${props => props.theme.breakpoints.tablet} {
    min-height: auto;
    margin-top: ${props => props.theme.margins.base_x10};
    margin-bottom: ${props => props.theme.margins.base_x4};
  }
`;

const Wrapper = styled.div`
  /** revert margin from ClientDashboard */
  margin: 0 -${props => props.theme.margins.base_x2};

  ${props => props.theme.breakpoints.tablet} {
    margin: 0;
  }
`;

FlowPanel.Title = Title;
FlowPanel.Description = Description;
FlowPanel.FullScreen = FullScreen;
FlowPanel.Wrapper = Wrapper;

export { FlowPanel };

const FlowPanelWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.margins.base};
`;

const Body = styled(OptionPanel)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  flex: 1 0 auto;
  padding: ${props => props.theme.margins.base_x3};
  padding-top: ${props => props.theme.margins.base_x2};
  margin: 0;

  ${props => props.theme.breakpoints.tablet} {
    padding: ${props => props.theme.margins.base_x6}
      ${props => props.theme.margins.base_x2}
      ${props => props.theme.margins.base_x8}
      ${props => props.theme.margins.base_x2};
  }
`;

const Footer = styled(OptionPanel)`
  flex: 0 0 auto;
  padding: ${props => props.theme.margins.base_x3};
  margin: 0;
`;

const FooterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 100%;
  margin: 0 auto;

  ${props => props.theme.breakpoints.tablet} {
    width: ${rem(640)};
  }

  & > :only-child {
    margin: 0 auto;
  }
`;

const BodyContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  text-align: center;

  ${props => props.theme.breakpoints.tablet} {
    width: ${rem(640)};
  }
`;

const BackButton = styled.button`
  all: unset;
  cursor: pointer;
`;

const ButtonRow = styled.div`
  width: 100%;
  margin-bottom: ${props => props.theme.margins.base_x2};
  margin-left: ${props => `-${props.theme.margins.base}`};
`;
